import { Logger } from "shared/lib/Logger"
import { getWindow } from "shared/lib/getWindow"

import "./etracker.types"

/**
 * Slot numbers as configured under Settings -> Account -> Custom Segments in the
 * eTracker account for my.schako.com. Changing these here without changing them
 * there silently mislabels the data.
 */
export const ETRACKER_SEGMENT = {
  NAME: 1,
  EMAIL: 2,
  DOMAIN: 3,
  SIGNED_IN: 4,
  COMPANY: 5
} as const

export type EtrackerSegmentSlot =
  (typeof ETRACKER_SEGMENT)[keyof typeof ETRACKER_SEGMENT]

/**
 * Sends a custom dimension value to eTracker.
 *
 * Silently does nothing when eTracker is unavailable — during prerendering, or
 * when the loader was blocked by an ad blocker or CSP. Those cases are expected
 * for a large share of visitors and deliberately not logged; an actual throw out
 * of the eTracker code is reported via Logger.warn.
 *
 * When the loader has not finished initialising yet (it is loaded with `async`),
 * the send is queued on `_etrackerOnReady`, which the loader drains on init.
 *
 * @see documentation/decisions/0023-etracker-user-segments.md
 */
export function sendCustomSegment(
  slot: EtrackerSegmentSlot,
  value: string
): void {
  try {
    const currentWindow = getWindow()

    if (!currentWindow) return

    const CustomSegmentEvent = currentWindow.et_CustomSegmentEvent

    if (typeof CustomSegmentEvent !== "function") return

    const segmentName = `seg${slot}` as const

    // Set the global too, so the value is attached to the page impressions the
    // loader tracks automatically via data-page-changed-detection="url".
    currentWindow[`et_${segmentName}`] = value

    const event = new CustomSegmentEvent(segmentName, value)

    const send = () => {
      try {
        getWindow()?._etracker?.sendEvent(event)
      } catch (error) {
        // Never let eTracker break the app — but do report it, since a throw out
        // of the eTracker code is unexpected and otherwise invisible.
        Logger.warn(error as Error, "eTracker sendEvent failed", {
          segmentName
        })
      }
    }

    if (currentWindow._etracker?.sendEvent) {
      send()
      return
    }

    currentWindow._etrackerOnReady = currentWindow._etrackerOnReady ?? []
    currentWindow._etrackerOnReady.push(send)
  } catch (error) {
    // Never let eTracker break the app — but do report it, since a throw out of
    // the eTracker code is unexpected and otherwise invisible.
    Logger.warn(error as Error, "eTracker custom segment failed", { slot })
  }
}
